import { Box } from '@mui/material';
import { Button, FormTextField, Grid, Typography } from 'ui';
import { DataConfigType } from 'routes/logged-in/collection-data-configs/CreateCollectionDataConfigRoute';
import { Form, Formik, FormikErrors } from 'formik';
import { FunctionComponent } from 'react';
import { consumer } from '@tock/coretypes';

interface LuceneDataConfigFormProps {
  dataConfigType: DataConfigType;
  onCancel(): void;
  onSuccess?(): void;
  onSubmit(values: LuceneDataConfigValues): Promise<void>;
  initialValues: LuceneDataConfigValues;
  editConfig?: consumer.ICollectionDataConfiguration;
}

export interface LuceneDataConfigValues {
  luceneQuery: string;
  name: string;
}

export const getDataConfigurationFromValues = (
  { luceneQuery, name }: LuceneDataConfigValues,
  dataConfigType: DataConfigType,
  editDataConfig?: consumer.ICollectionDataConfiguration
): consumer.ICollectionDataConfiguration => {
  switch (dataConfigType) {
    case 'businessLucene':
      return {
        ...editDataConfig,
        name,
        businessLuceneSearch: { luceneQuery },
      };
    case 'experienceLucene':
      return {
        ...editDataConfig,
        name,
        experienceLuceneSearch: { luceneQuery },
      };
    default:
      return {};
  }
};

export const luceneDataConfigEmptyState = { luceneQuery: '*', name: '' };

const LuceneDataConfigForm: FunctionComponent<
  React.PropsWithChildren<LuceneDataConfigFormProps>
> = ({ dataConfigType, onCancel, initialValues, onSubmit, editConfig }) => {
  const getTitle = () => {
    switch (dataConfigType) {
      case 'businessLucene':
        return editConfig
          ? `Edit Business Lucene Search ${editConfig.name}`
          : 'Create Business Lucene Search';
      case 'experienceLucene':
        return editConfig
          ? `Edit Experience Lucene Search ${editConfig.name}`
          : 'Create Experience Lucene Search';

      default:
        return '';
    }
  };

  const validate = (values: LuceneDataConfigValues): FormikErrors<LuceneDataConfigValues> => {
    const errors: FormikErrors<LuceneDataConfigValues> = {};

    if (!values.name) {
      errors.name = 'Name is required';
    }

    if (!values.luceneQuery) {
      errors.luceneQuery = 'Lucene query is required';
    }

    return errors;
  };

  return (
    <>
      <Typography variant="heading1Medium">{getTitle()}</Typography>
      <Formik<{ luceneQuery: string; name: string }>
        enableReinitialize
        initialValues={initialValues}
        onSubmit={onSubmit}
        validate={validate}
      >
        <Form>
          <Box margin={2}>
            <Grid spacing={2} container>
              <Grid item xs={12}>
                <FormTextField label="Name" name="name" fullWidth />
              </Grid>
              <Grid item xs={12}>
                <FormTextField
                  label="Lucene Query"
                  multiline
                  name="luceneQuery"
                  fullWidth
                  minRows={4}
                />
              </Grid>
              <Grid item>
                <Button type="submit">Submit</Button>
              </Grid>
              <Grid item>
                <Button variant="outlined" onClick={onCancel}>
                  Cancel
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Form>
      </Formik>
    </>
  );
};

export default LuceneDataConfigForm;
